import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';

// Public / marketing pages
import Header from './components/Header';
import Hero from './components/Hero';
import PracticeByTime from './components/PracticeByTime';
import CommunityStrip from './components/CommunityStrip';
import QuoteCarousel from './components/QuoteCarousel';
import Events from './components/Events';
import AboutAshram from './components/AboutAshram';
import EcoStore from './components/EcoStore';
import FeaturedRecap from './components/FeaturedRecap';
import AboutOsho from './components/AboutOsho';
import CommunityJoin from './components/CommunityJoin'; // eslint-disable-line no-unused-vars
import Testimonials from './components/Testimonials';
import FeaturedBlog from './components/FeaturedBlog';
import DonateCTA from './components/DonateCTA';
import Footer from './components/Footer';

// Auth pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import MembershipPage from './pages/MembershipPage';
import OshoLibrary from './pages/OshoLibrary';
import CopyrightPolicy from './pages/CopyrightPolicy';
import AskGuide from './pages/AskGuide';
import ChatWidget from './components/ChatWidget';
import EventsPage from './pages/EventsPage';
import ProgramDetail from './pages/ProgramDetail';
import CentersDirectory from './pages/CentersDirectory';
import CenterDetail from './pages/CenterDetail';
import AboutUs from './pages/AboutUs';
import WhoIsOsho from './pages/WhoIsOsho';
import AboutFounder from './pages/AboutFounder';
import MahimaTrivedi from './pages/MahimaTrivedi';
import AboutAnandArun from './pages/AboutAnandArun';
import ContactUs from './pages/ContactUs';
import BlogPage from './pages/BlogPage';
import BlogPost from './pages/BlogPost';
import DonatePage from './pages/DonatePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import AdminBlog from './pages/dashboard/AdminBlog';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';

// Dashboard pages
import DashboardLayout from './layouts/DashboardLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import ProfilePage from './pages/dashboard/ProfilePage';
import FacilitatorPanel from './pages/dashboard/FacilitatorPanel'; // eslint-disable-line no-unused-vars
import CoordinatorPanel from './pages/dashboard/CoordinatorPanel';
import AdminHome from './pages/dashboard/AdminHome';
import AdminUsers from './pages/dashboard/AdminUsers';
import AdminApprovals from './pages/dashboard/AdminApprovals';
import AdminMemberships from './pages/dashboard/AdminMemberships';
import MyPrograms from './pages/dashboard/MyPrograms';
import AdminPrograms from './pages/dashboard/AdminPrograms';
import MyBookings from './pages/dashboard/MyBookings';
import PaymentHistory from './pages/dashboard/PaymentHistory';
import MyCenters from './pages/dashboard/MyCenters';
import AdminCenters from './pages/dashboard/AdminCenters';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import TeachersDirectory from './pages/TeachersDirectory';
import Meditations from './pages/Meditations';
import MeditationDetail from './pages/MeditationDetail';
import AdminMeditations from './pages/dashboard/AdminMeditations';
import AdminLibrary from './pages/dashboard/AdminLibrary';
import AdminLegal from './pages/dashboard/AdminLegal';
import AdminChat from './pages/dashboard/AdminChat';
import AdminAIUsage from './pages/dashboard/AdminAIUsage';
import AdminAssistant from './pages/dashboard/AdminAssistant';
import ContentStudio from './pages/dashboard/ContentStudio';
import ConsultationRequests from './pages/dashboard/ConsultationRequests';
import AdminConsultations from './pages/dashboard/AdminConsultations';
import TeacherDetail from './pages/TeacherDetail';
import FacilitatorDetail from './pages/FacilitatorDetail'; // eslint-disable-line no-unused-vars
import FacilitatorRedirect from './pages/FacilitatorRedirect';
import ShopPage from './pages/ShopPage';
import ProductDetail from './pages/ProductDetail';
import CartPage from './pages/CartPage';
import OrderSuccess from './pages/OrderSuccess';
import AdminTeachers from './pages/dashboard/AdminTeachers';
import AdminLive from './pages/dashboard/AdminLive';
import AdminHomeHero from './pages/dashboard/AdminHomeHero';
import LiveStream from './pages/LiveStream';
import AdminShop from './pages/dashboard/AdminShop';
import MyOrders from './pages/dashboard/MyOrders';
import MyTeacherProfile from './pages/dashboard/MyTeacherProfile';
import AdminCommunity from './pages/dashboard/AdminCommunity';
import MyLive from './pages/dashboard/MyLive';
import MembersLive from './pages/MembersLive';
import ManageGiving from './pages/ManageGiving';
import AdminNotifications from './pages/dashboard/AdminNotifications';
import AdminEventPhotos from './pages/dashboard/AdminEventPhotos';
import AdminDonations from './pages/dashboard/AdminDonations';
import AdminEnquiries from './pages/dashboard/AdminEnquiries';
import AdminPayments from './pages/dashboard/AdminPayments';
import AdminCheckin from './pages/dashboard/AdminCheckin';
import AdminCustomers from './pages/dashboard/AdminCustomers';
import AdminInbox from './pages/dashboard/AdminInbox';
import ShareExperience from './pages/dashboard/ShareExperience';
import AdminTestimonials from './pages/dashboard/AdminTestimonials';
import CommunityFeed from './pages/CommunityFeed';

const Home = () => (
  <div className="App">
    <Header />
    <main>
      <Hero />
      <PracticeByTime />
      <Events />
      <QuoteCarousel />
      <CommunityStrip />
      <FeaturedBlog />
      <FeaturedRecap />
      <Testimonials />
      <EcoStore />
      <AboutAshram />
      <AboutOsho />
      <DonateCTA />
    </main>
    <Footer />
  </div>
);

// Handles Google OAuth callback (session_id in URL fragment) before normal routing
const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/membership" element={<MembershipPage />} />
      <Route path="/osho-library" element={<OshoLibrary />} />
      <Route path="/copyright-policy" element={<CopyrightPolicy />} />
      <Route path="/ask" element={<AskGuide />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/live" element={<LiveStream />} />
      <Route path="/members/live" element={<MembersLive />} />
      <Route path="/giving/manage" element={<ManageGiving />} />
      <Route path="/members/live/:id" element={<MembersLive />} />
      <Route path="/programs/:slug" element={<ProgramDetail />} />
      <Route path="/centers" element={<CentersDirectory variant="community" />} />
      <Route path="/temples" element={<CentersDirectory variant="temples" />} />
      <Route path="/centers/:slug" element={<CenterDetail />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/who-osho" element={<WhoIsOsho />} />
      <Route path="/about-prem" element={<AboutFounder />} />
      <Route path="/co-founder-mahima" element={<MahimaTrivedi />} />
      <Route path="/bodhisattvaswami-arun" element={<AboutAnandArun />} />
      <Route path="/contacts" element={<ContactUs />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog-page" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/donate" element={<DonatePage />} />
      <Route path="/terms-and-conditions" element={<TermsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPage />} />
      <Route path="/facilitator-and-teachers" element={<TeachersDirectory />} />
      <Route path="/meditations" element={<Meditations />} />
      <Route path="/meditations/:slug" element={<MeditationDetail />} />
      <Route path="/teachers" element={<TeachersDirectory />} />
      <Route path="/teachers/:slug" element={<TeacherDetail />} />
      <Route path="/facilitators/:id" element={<FacilitatorRedirect />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/community" element={<CommunityFeed />} />      <Route path="/product/:slug" element={<ProductDetail />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/order/success" element={<OrderSuccess />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Dashboard (protected) */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="bookings" element={<MyBookings />} />
        <Route path="orders" element={<MyOrders />} />
        <Route path="payments" element={<PaymentHistory />} />
        <Route path="share-experience" element={<ShareExperience />} />
        <Route
          path="studio"
          element={
            <ProtectedRoute roles={["admin", "content_contributor"]}><ContentStudio /></ProtectedRoute>
          }
        />
        <Route
          path="programs"
          element={
            <ProtectedRoute roles={["admin", "facilitator", "ashram_coord", "center_coord"]}><MyPrograms /></ProtectedRoute>
          }
        />
        <Route
          path="my-live"
          element={
            <ProtectedRoute roles={["admin", "facilitator"]}><MyLive /></ProtectedRoute>
          }
        />
        <Route
          path="centers"
          element={<MyCenters />}
        />
        <Route
          path="teacher-application"
          element={<MyTeacherProfile />}
        />
        <Route
          path="facilitator"
          element={<MyTeacherProfile />}
        />
        <Route
          path="coordinator"
          element={<CoordinatorPanel />}
        />
        <Route
          path="consultations"
          element={<ConsultationRequests />}
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute roles={["admin"]}><AdminHome /></ProtectedRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>
          }
        />
        <Route
          path="admin/customers"
          element={
            <ProtectedRoute roles={["admin"]}><AdminCustomers /></ProtectedRoute>
          }
        />
        <Route
          path="admin/inbox"
          element={
            <ProtectedRoute roles={["admin"]}><AdminInbox /></ProtectedRoute>
          }
        />
        <Route
          path="admin/approvals"
          element={
            <ProtectedRoute roles={["admin"]}><AdminApprovals /></ProtectedRoute>
          }
        />
        <Route
          path="admin/memberships"
          element={
            <ProtectedRoute roles={["admin"]}><AdminMemberships /></ProtectedRoute>
          }
        />
        <Route
          path="admin/programs"
          element={
            <ProtectedRoute roles={["admin"]}><AdminPrograms /></ProtectedRoute>
          }
        />
        <Route
          path="admin/checkin"
          element={
            <ProtectedRoute roles={["admin"]}><AdminCheckin /></ProtectedRoute>
          }
        />
        <Route
          path="admin/payments"
          element={
            <ProtectedRoute roles={["admin"]}><AdminPayments /></ProtectedRoute>
          }
        />
        <Route
          path="admin/testimonials"
          element={
            <ProtectedRoute roles={["admin"]}><AdminTestimonials /></ProtectedRoute>
          }
        />
        <Route
          path="admin/centers"
          element={
            <ProtectedRoute roles={["admin"]}><AdminCenters /></ProtectedRoute>
          }
        />
        <Route
          path="admin/blog"
          element={
            <ProtectedRoute roles={["admin"]}><AdminBlog /></ProtectedRoute>
          }
        />
        <Route
          path="admin/teachers"
          element={
            <ProtectedRoute roles={["admin"]}><AdminTeachers /></ProtectedRoute>
          }
        />
        <Route
          path="admin/meditations"
          element={
            <ProtectedRoute roles={["admin"]}><AdminMeditations /></ProtectedRoute>
          }
        />
        <Route
          path="admin/library"
          element={
            <ProtectedRoute roles={["admin"]}><AdminLibrary /></ProtectedRoute>
          }
        />
        <Route
          path="admin/legal"
          element={
            <ProtectedRoute roles={["admin"]}><AdminLegal /></ProtectedRoute>
          }
        />
        <Route
          path="admin/chat"
          element={
            <ProtectedRoute roles={["admin"]}><AdminChat /></ProtectedRoute>
          }
        />
        <Route
          path="admin/ai-usage"
          element={
            <ProtectedRoute roles={["admin"]}><AdminAIUsage /></ProtectedRoute>
          }
        />
        <Route
          path="admin/assistant"
          element={
            <ProtectedRoute roles={["admin"]}><AdminAssistant /></ProtectedRoute>
          }
        />
        <Route
          path="admin/consultations"
          element={
            <ProtectedRoute roles={["admin"]}><AdminConsultations /></ProtectedRoute>
          }
        />
        <Route
          path="admin/notifications"
          element={
            <ProtectedRoute roles={["admin"]}><AdminNotifications /></ProtectedRoute>
          }
        />
        <Route
          path="admin/live"
          element={
            <ProtectedRoute roles={["admin"]}><AdminLive /></ProtectedRoute>
          }
        />
        <Route
          path="admin/home-hero"
          element={
            <ProtectedRoute roles={["admin"]}><AdminHomeHero /></ProtectedRoute>
          }
        />
        <Route
          path="admin/shop"
          element={
            <ProtectedRoute roles={["admin"]}><AdminShop /></ProtectedRoute>
          }
        />
        <Route
          path="admin/community"
          element={
            <ProtectedRoute roles={["admin"]}><AdminCommunity /></ProtectedRoute>
          }
        />
        <Route
          path="admin/event-photos"
          element={
            <ProtectedRoute roles={["admin"]}><AdminEventPhotos /></ProtectedRoute>
          }
        />
        <Route
          path="admin/donations"
          element={
            <ProtectedRoute roles={["admin"]}><AdminDonations /></ProtectedRoute>
          }
        />
        <Route
          path="admin/enquiries"
          element={
            <ProtectedRoute roles={["admin"]}><AdminEnquiries /></ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<Home />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <LanguageProvider>
            <AppRouter />
            <ChatWidget />
            <Toaster />
          </LanguageProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
